'use client'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'
import { useSearchParams } from 'next/navigation'
import { SpinnerIcon } from 'primitives/icons'
import * as React from 'react'
import type { UseInfiniteHitsProps } from 'react-instantsearch'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import type { ClickHandlerProps } from 'react-photo-album'
import type { AlgoliaSearchedProductsLocalStorage } from 'algolia/algolia'
import { algoliaAnalytics, AlgoliaEventName } from 'algolia/algolia'
import type { AlgoliaContentHit } from 'algolia/algolia-content'
import { algoliaContentIndex } from 'algolia/algolia-content'
import { defaultLanguage, mapContentLanguageToLocale } from 'cf-i18n/constants'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useUserId } from '@repo/cf-user/hooks/use-user-data'
import { useFeatureIsOn } from 'growthbook'
import Masonry from 'react-masonry-css'
import { localStore } from 'utils/storage'

import type { PhotoAlbumPhoto } from '@web/components/photo-album'
import { PhotoAlbum } from '@web/components/photo-album'
import { useSearchPageContext } from '@web/components/algolia/providers/content-api-search-page-provider'
import { NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL } from '@web/utils/env'
import { AlbumProvider } from '@web/components/content-api/album-context'
import {
  FEATURE_TRIAL_OFFER_ON_PRODUCTS_CARDS_AI_CATEGORY_PAGES,
  FEATURE_TRIAL_OFFER_ON_PRODUCTS_CARDS_STOCKPHOTOS_CATEGORY_PAGES,
} from '@web/utils/features'
import { ProductCard } from '@web/components/product-card/product-card'
import type { CategoryPageType } from '@web/components/content-api/config'
import { mapCategoryPageTypeToCategoryDetails } from '@web/components/content-api/config'

const transformItems: UseInfiniteHitsProps<AlgoliaContentHit>['transformItems'] = hits => {
  return hits.filter(hit => hit.thumbnail.width > 0 && hit.thumbnail.height > 0)
}

const transformItemsPreview: UseInfiniteHitsProps<AlgoliaContentHit>['transformItems'] = hits => {
  return hits.filter(
    hit => hit.previewMobile && hit.previewMobile.width > 0 && hit.previewMobile.height > 0,
  )
}

export const ALGOLIA_SEARCHED_PRODUCTS_LOCAL_STORAGE_KEY = 'algoliaSearchedProducts'
const sevenDays = 7 * 24 * 60 * 60 * 1000

export function ListProducts({
  showPreviousText,
  usePreview = false,
  category,
}: {
  showPreviousText: string
  usePreview: boolean
  category: CategoryPageType
}) {
  const searchParams = useSearchParams()
  const currentRefinement = searchParams?.get('sortBy') || algoliaContentIndex
  const { hits, showMore, isLastPage, isFirstPage, showPrevious } =
    useInfiniteHits<AlgoliaContentHit>({
      transformItems: usePreview ? transformItemsPreview : transformItems,
    })

  const { status, indexRenderState } = useInstantSearch()
  //If results are cached, algolia needs a bit of time to initialize the state
  //While the state is not initialized, we show a spinner and do not try to fetch the next page
  const algoliaStateInitialized = Object.keys(indexRenderState).length > 0

  const { contentLanguage, categoryName } = useSearchPageContext()
  // eslint-disable-next-line cf-web/codegen-query-patterns
  const isUserLoggedIn = useIsLoggedInQuery()
  const userId = useUserId()

  const locale = mapContentLanguageToLocale.get(contentLanguage) || defaultLanguage

  const isTrialOfferOnProductsCardsAiCategoryPages = useFeatureIsOn(
    FEATURE_TRIAL_OFFER_ON_PRODUCTS_CARDS_AI_CATEGORY_PAGES,
    true,
  )

  const isTrialOfferOnProductsCardsStockphotosCategoryPages = useFeatureIsOn(
    FEATURE_TRIAL_OFFER_ON_PRODUCTS_CARDS_STOCKPHOTOS_CATEGORY_PAGES,
    true,
  )

  const isStockphotos = category === mapCategoryPageTypeToCategoryDetails.stockphotos.authorId

  const photos: PhotoAlbumPhoto[] = hits.map(hit => {
    const href = `${locale === defaultLanguage ? '' : `/${locale}`}${AppRoutes.PROD}${
      hit.slug ? hit.slug[contentLanguage] : ''
    }-${hit.objectID}/`

    return {
      src: `${NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL}/${
        usePreview ? hit.previewMobile.url : hit.thumbnail.url
      }`,
      width: usePreview ? hit.previewMobile.width : hit.thumbnail.width,
      height: usePreview ? hit.previewMobile.height : hit.thumbnail.height,
      alt: hit.title ? `${hit.title[contentLanguage]}${usePreview ? ` ${categoryName}` : ''}` : '',
      title: hit.title
        ? `${hit.title[contentLanguage]}${usePreview ? ` ${categoryName}` : ''}`
        : '',
      href: href,
      queryId: hit.__queryID,
      position: hit.__position,
      id: hit.objectID,
    }
  })
  const photoIds = React.useMemo(() => photos.map(photo => photo.id), [photos])

  const handlePhotoClick = ({ photo }: ClickHandlerProps<PhotoAlbumPhoto>) => {
    if (!photo.href) {
      return
    }

    const prodId = String(photo.href).split('-').pop()?.replace('/', '')

    if (prodId) {
      algoliaAnalytics('clickedObjectIDsAfterSearch', {
        index: algoliaContentIndex,
        eventName: AlgoliaEventName.PRODUCT_CLICKED,
        queryID: photo.queryId || '',
        objectIDs: [prodId],
        positions: [photo.position || 0],
      })

      const today = new Date()
      const futureDate = new Date(today.getTime() + sevenDays)

      let algoliaSearchedProducts: AlgoliaSearchedProductsLocalStorage | null = null

      const localStorageKey = `${ALGOLIA_SEARCHED_PRODUCTS_LOCAL_STORAGE_KEY}.${
        isUserLoggedIn.data ? userId : 'guest'
      }`

      try {
        const localStorageValue = localStore.getString(localStorageKey) || null
        if (localStorageValue) {
          algoliaSearchedProducts = JSON.parse(localStorageValue)
        }
      } catch (error) {
        //local storage is not available so we can't store the clicked products
        return
      }

      if (
        !algoliaSearchedProducts ||
        new Date(algoliaSearchedProducts.expiresAt).getTime() < today.getTime() - sevenDays
      ) {
        const newAlgoliaSearchedProducts = {
          items: { [currentRefinement]: { [prodId]: photo.queryId || '' } },
          expiresAt: futureDate,
        }
        try {
          localStore.setJSON(localStorageKey, newAlgoliaSearchedProducts)
        } catch (error) {
          return
        }
      } else {
        const newAlgoliaSearchedProducts = { ...algoliaSearchedProducts }

        // oxlint-disable-next-line no-negated-condition
        if (!newAlgoliaSearchedProducts.items[currentRefinement]) {
          newAlgoliaSearchedProducts.items[currentRefinement] = { [prodId]: photo.queryId || '' }
        } else {
          newAlgoliaSearchedProducts.items[currentRefinement] = {
            ...newAlgoliaSearchedProducts.items[currentRefinement],
            [prodId]: photo.queryId || '',
          }
        }

        newAlgoliaSearchedProducts.expiresAt = futureDate
        try {
          localStore.setJSON(localStorageKey, newAlgoliaSearchedProducts)
        } catch (error) {
          return
        }
      }
    }
  }

  const targetRowHeight = 220

  return (
    <>
      {!isFirstPage ? (
        <div className="flex w-full justify-center">
          <button
            className="bg-primary-100 text-14 text-primary-500 w-fit rounded px-5 py-[10px] font-bold uppercase"
            onClick={showPrevious}
          >
            {showPreviousText}
          </button>
        </div>
      ) : null}
      <div className="mt-5">
        {(
          isStockphotos
            ? isTrialOfferOnProductsCardsStockphotosCategoryPages
            : isTrialOfferOnProductsCardsAiCategoryPages
        ) ? (
          <Masonry
            className="flex gap-[10px]"
            columnClassName="flex flex-col gap-[10px] min-w-0"
            breakpointCols={{ 640: 1, 1024: 2, 1280: 3, default: 4 }}
          >
            {photos.map(photo => (
              <ProductCard
                key={photo.id}
                imageClassName="aspect-square object-contain bg-cf-dreamy-cloud-400"
                productId={photo.id}
                productUrl={photo.href}
                image={photo.src}
                imageWidth={photo.width}
                imageHeight={photo.height}
                alt={photo.alt}
                name={photo.title || ''}
                category={category}
                linkInNewTab
                onClick={() => handlePhotoClick({ photo } as ClickHandlerProps<PhotoAlbumPhoto>)}
              />
            ))}
          </Masonry>
        ) : (
          <AlbumProvider photoIds={photoIds}>
            <PhotoAlbum
              layout="rows"
              photos={photos}
              rowConstraints={containerWidth => {
                if (containerWidth < 440) {
                  return { minPhotos: 1, maxPhotos: 1, singleRowMaxHeight: targetRowHeight }
                }
                if (containerWidth < 640) {
                  return { minPhotos: 1, singleRowMaxHeight: targetRowHeight }
                }
                if (containerWidth < 1024) {
                  return { minPhotos: 2, singleRowMaxHeight: targetRowHeight }
                }
                if (containerWidth < 1280) {
                  return { minPhotos: 3, singleRowMaxHeight: targetRowHeight }
                }
                return { minPhotos: 4, singleRowMaxHeight: targetRowHeight }
              }}
              spacing={2}
              targetRowHeight={targetRowHeight}
              onClick={handlePhotoClick}
              openPhotoInNewTab={true}
            />
          </AlbumProvider>
        )}
      </div>

      {status === 'loading' || status === 'stalled' || !algoliaStateInitialized ? (
        <div className="mt-5 flex h-full w-full items-center justify-center">
          <SpinnerIcon className="h-[23px] w-[23px] animate-spin" />
        </div>
      ) : (
        <LoadMoreTrigger showMore={showMore} canLoadMore={hits.length > 0 ? !isLastPage : false} />
      )}
    </>
  )
}

export function LoadMoreTrigger({
  canLoadMore,
  showMore,
}: {
  canLoadMore: boolean
  showMore: () => void
}) {
  const [ref, entry] = useIntersectionObserver<HTMLDivElement>({
    // oxlint-disable-next-line no-negated-condition
    rootMargin: typeof window !== 'undefined' ? `${window.innerHeight}px` : '0px',
  })

  const shouldShowMore = entry?.isIntersecting && canLoadMore

  React.useEffect(() => {
    if (shouldShowMore) {
      showMore()
    }
  }, [shouldShowMore, showMore])

  return <div ref={ref} className="h-px w-full bg-transparent" />
}
